import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Filter, Loader2, User } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useCustomers } from "../hooks/useCustomers";
import type { CustomerDetailResponse } from "../types/customers";

interface CustomerMultiSelectFilterProps {
  organizationIds: string[];
  selectedIds: string[];
  selectedCustomers: CustomerDetailResponse[];
  onChange: (ids: string[]) => void;
  showOrganization?: boolean;
  disabled?: boolean;
}

export function CustomerMultiSelectFilter({
  organizationIds,
  selectedIds,
  selectedCustomers,
  onChange,
  showOrganization = false,
  disabled = false,
}: CustomerMultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Always pass an org-scoped filter object (never undefined) so the shared hook
  // doesn't fall back to fetching the unfiltered, cross-org customer list.
  // Gating on `open` prevents the request from firing until the user actually
  // opens the picker.
  const { data: searchData, isLoading } = useCustomers(
    {
      searchQuery: debouncedSearch || undefined,
      organizationIds,
      pageSize: 20,
    },
    { enabled: open }
  );

  // Ref-backed selection so cmdk's cached onSelect handlers always see fresh state.
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const toggle = useCallback(
    (id: string) => {
      const current = selectedIdsRef.current;
      const next = current.includes(id)
        ? current.filter((v) => v !== id)
        : [...current, id];
      onChange(next);
    },
    [onChange]
  );

  const clearAll = useCallback(() => onChange([]), [onChange]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hasSelection = selectedIds.length > 0;

  // Union of label-resolved selected customers + search results, deduped by id, alphabetical.
  // Sorting only by name (not selected-first) prevents rows from jumping on toggle.
  const combined = useMemo(() => {
    const byId = new Map<string, CustomerDetailResponse>();
    for (const c of selectedCustomers) byId.set(c.id, c);
    for (const c of searchData?.customers || []) {
      if (!byId.has(c.id)) byId.set(c.id, c);
    }
    return [...byId.values()].sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [selectedCustomers, searchData]);

  const isEmpty = combined.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between transition-all duration-200",
            hasSelection && "border-primary border-2 bg-primary/5"
          )}
        >
          <div className="flex items-center min-w-0">
            <Filter
              className={cn(
                "mr-2 h-4 w-4 flex-shrink-0",
                hasSelection ? "text-primary" : "text-muted-foreground"
              )}
            />
            <span className="truncate">
              Customer
              {hasSelection && ` (${selectedIds.length})`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search customers..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          {hasSelection && (
            <div className="flex items-center justify-between px-2 py-1.5 border-b">
              <span className="text-xs text-muted-foreground">
                {selectedIds.length} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
              >
                Clear
              </Button>
            </div>
          )}
          <CommandList>
            {isLoading && isEmpty ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-4 w-4 animate-spin mb-2" />
                Searching...
              </div>
            ) : isEmpty && debouncedSearch.length > 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No customers found for "{debouncedSearch}"
              </div>
            ) : isEmpty ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type to search customers
              </div>
            ) : (
              <CommandGroup>
                {combined.map((customer) => {
                  const checked = selectedIdSet.has(customer.id);
                  return (
                    <CommandItem
                      key={customer.id}
                      value={customer.id}
                      onSelect={() => toggle(customer.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{customer.fullName}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {customer.email}
                          </p>
                          {showOrganization && customer.organizationName && (
                            <p className="text-xs text-muted-foreground truncate">
                              {customer.organizationName}
                            </p>
                          )}
                        </div>
                        {checked && (
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
